import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import {
  ClipboardCheck,
  Image as ImageIcon,
  LayoutDashboard,
  Megaphone,
  Receipt,
  Wallet,
  type LucideIcon,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { api } from "../../convex/_generated/api";
import type { FunctionReturnType } from "convex/server";
import type { Doc } from "../../convex/_generated/dataModel";
import { Authenticated, AuthLoading, Unauthenticated } from "@/components/auth-gate";
import { Balance } from "@/components/brands/balance";
import { Campaigns } from "@/components/brands/campaigns";
import { Overview } from "@/components/brands/overview";
import { BrandsPitch } from "@/components/brands/pitch";
import { Products } from "@/components/brands/products";
import { Review } from "@/components/brands/review";
import { Sales } from "@/components/brands/sales";
import { Money } from "@/components/brands/ui";
import { Splash } from "@/components/shell/splash";
import { Wordmark } from "@/components/shell/wordmark";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster } from "@/components/ui/sonner";
import { authClient } from "@/lib/auth-client";
import { said } from "@/lib/errors";
import { organizationSlug } from "@/lib/organizations";
import { cn } from "@/lib/utils";

const TABS = ["overview", "campaigns", "products", "sales", "balance", "review"] as const;
type Tab = (typeof TABS)[number];

const ICONS: Record<Tab, LucideIcon> = {
  overview: LayoutDashboard,
  campaigns: Megaphone,
  products: ImageIcon,
  sales: Receipt,
  balance: Wallet,
  review: ClipboardCheck,
};

export const Route = createFileRoute("/brands")({
  validateSearch: (search: Record<string, unknown>): { tab?: Tab; topup?: "success" } => ({
    ...(TABS.includes(search.tab as Tab) && { tab: search.tab as Tab }),
    ...(search.topup === "success" && { topup: "success" as const }),
  }),
  component: BrandsPage,
});

function BrandsPage() {
  const { t: tc } = useTranslation();
  return (
    <>
      <AuthLoading>
        <Splash subtitle={tc("status.loading")} />
      </AuthLoading>
      <Unauthenticated>
        <BrandsPitch />
      </Unauthenticated>
      <Authenticated>
        <Console />
      </Authenticated>
      <Toaster />
    </>
  );
}

function Console() {
  const { t: tc } = useTranslation();
  const mine = useQuery(api.brands.mine);
  if (mine === undefined) return <Splash subtitle={tc("status.loading")} />;
  // A reviewer needs no brand of their own to do the reviewing.
  if (!mine?.brand && !mine?.reviewer) return <Setup company={mine?.company ?? null} />;
  return <Dashboard brand={mine.brand} review={mine.review} reviewer={mine.reviewer} />;
}

type Mine = NonNullable<FunctionReturnType<typeof api.brands.mine>>;

/**
 * Sets the brand's company up (a Better Auth organization, waiting for Ours'
 * approval), then its console. A company already there, from a setup cut
 * short, keeps its name.
 */
function Setup({ company }: { company: Mine["company"] }) {
  const { t } = useTranslation("brands");
  const create = useMutation(api.brands.create);
  const [name, setName] = useState(company?.name ?? "");
  const [website, setWebsite] = useState("");
  const [saving, setSaving] = useState(false);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setSaving(true);
    try {
      if (!company) {
        const { error } = await authClient.organization.create({
          name: name.trim(),
          slug: organizationSlug(name),
          kind: "brand",
          website: website.trim(),
          // The console is opened next, not a company switcher.
          keepCurrentActiveOrganization: true,
        });
        if (error) throw new Error(error.message);
      }
      await create({ website });
    } catch (error) {
      toast.error(said(error, t("setup.error")));
      setSaving(false);
    }
  }

  return (
    <main className="mx-auto grid min-h-svh max-w-md content-center gap-6 px-6">
      <Wordmark size="md" />
      <div className="grid gap-2">
        <h1 className="text-xl font-semibold text-ink">{t("setup.title")}</h1>
        <p className="text-sm text-ink-2">{t("setup.body")}</p>
      </div>
      <form onSubmit={(event) => void submit(event)} className="grid gap-3.5">
        <div className="grid gap-1.5">
          <Label htmlFor="brand-name">{t("setup.name")}</Label>
          <Input
            id="brand-name"
            value={name}
            maxLength={80}
            disabled={company !== null}
            placeholder={t("setup.namePlaceholder")}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="brand-website">{t("setup.website")}</Label>
          <Input
            id="brand-website"
            type="url"
            value={website}
            placeholder={t("setup.websitePlaceholder")}
            onChange={(event) => setWebsite(event.target.value)}
          />
        </div>
        <Button type="submit" disabled={!name.trim() || !website.trim() || saving}>
          {t("setup.submit")}
        </Button>
      </form>
    </main>
  );
}

/** A working screen rather than a page of Ours: a sidebar, tables and numbers. */
function Dashboard({
  brand,
  review,
  reviewer,
}: {
  brand: Doc<"brands"> | null;
  review: Mine["review"];
  reviewer: boolean;
}) {
  const { t } = useTranslation("brands");
  const { tab: asked, topup } = Route.useSearch();
  const tabs = TABS.filter((name) => (name === "review" ? reviewer : brand !== null));
  const tab = asked && tabs.includes(asked) ? asked : tabs[0]!;

  return (
    <div className="flex min-h-svh flex-col bg-paper-2 md:flex-row">
      <aside className="flex shrink-0 flex-col border-b border-line bg-paper md:sticky md:top-0 md:h-svh md:w-56 md:border-b-0 md:border-e">
        <div className="flex h-12 items-center gap-2 border-b border-line px-4">
          <Wordmark size="sm" />
          <span className="text-xs text-ink-3">{t("title")}</span>
        </div>
        <nav className="flex gap-1 overflow-x-auto p-2 md:flex-1 md:flex-col md:overflow-visible">
          {tabs.map((name) => {
            const Icon = ICONS[name];
            return (
              <Link
                key={name}
                to="/brands"
                search={{ tab: name }}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap rounded-md px-2.5 py-1.5 text-sm transition-colors",
                  tab === name
                    ? "bg-paper-3 font-medium text-ink"
                    : "text-ink-2 hover:bg-paper-2 hover:text-ink",
                )}
              >
                <Icon className="size-4" />
                {t(`nav.${name}`)}
              </Link>
            );
          })}
        </nav>
        {brand ? (
          <div className="hidden border-t border-line px-4 py-3 md:block">
            <p className="truncate text-sm font-medium text-ink">{brand.name}</p>
            <p className="text-xs text-ink-3">
              {t("balance.current")}: <Money cents={brand.balanceCents} className="text-ink" />
            </p>
          </div>
        ) : null}
      </aside>
      <main className="min-w-0 flex-1 px-5 py-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          {brand && review && tab !== "review" ? <CompanyReview review={review} /> : null}
          {tab === "overview" ? <Overview /> : null}
          {tab === "campaigns" && brand ? <Campaigns brand={brand} /> : null}
          {tab === "products" ? <Products /> : null}
          {tab === "sales" ? <Sales /> : null}
          {tab === "balance" && brand ? <Balance brand={brand} paid={topup === "success"} /> : null}
          {tab === "review" ? <Review /> : null}
        </div>
      </main>
    </div>
  );
}

/** Until Ours has approved the brand's company: what waits, or why it was turned down. */
function CompanyReview({ review }: { review: NonNullable<Mine["review"]> }) {
  const { t } = useTranslation("brands");
  if (review.status === "approved") return null;
  const rejected = review.status === "rejected";
  return (
    <div
      role="status"
      className={cn(
        "mb-5 rounded-md border px-4 py-3 text-sm",
        rejected ? "border-red-200 bg-red-50 text-red-900" : "border-line bg-paper text-ink-2",
      )}
    >
      {rejected ? (
        <>
          <p>{t("company.rejected", { reason: "rejectedBecause" in review ? review.rejectedBecause : "" })}</p>
          <p className="mt-1 text-xs">{t("company.rejectedContact")}</p>
        </>
      ) : (
        t("company.pending")
      )}
    </div>
  );
}
